import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useCurrentMemberContext } from 'src/contexts';
import { IEmployeeAbsence, IEmployeeAbsences } from 'src/pages/dashboard/dasboard.interfaces';

const GET_GANTTS_ABSENCE_BY_DATE = gql`
  query getGanttAbsenceRecordsByDate($payload: GetGanttRecordsByDateInput!) {
    getGanttAbsenceRecordsByDate(payload: $payload) {
      collection {
        _id
        entries {
          _id
          endDate
          endDateString
          startDate
          startDateString
          plannedEntryHours
        }
        employee {
          _id
          createdBy
          nickname
          firstName
          lastName
          ganttId
          avatarUrl
        }
        absenceReason {
          name
        }
      }
      totalCount
    }
  }
`;

const transformAbsences = (absence: IEmployeeAbsence) => ({
  employee: {
    avatar: absence.employee?.avatarUrl,
    name: `${absence.employee.firstName} ${absence.employee.lastName}`,
    id: absence.employee._id,
    ganttId: absence.employee.ganttId,
  },
  date: {
    startDate: absence.entries[0].startDateString,
    endDate: absence.entries[0].endDateString,
  },
  reason: absence.absenceReason.name,
});

export const useGetGanttAbsenceByDate = (startDate: Date, endDate: Date) => {
  const [absencesList, setAbsencesList] = useState<IEmployeeAbsences[]>([]);

  const [currentPage, setPage] = useState(1);

  const [currentNameSort, setNameSort] = useState<{ field: string; sortBy: number }[]>();
  const [isSorted, setIsSorted] = useState(false);

  const { ganttId } = useCurrentMemberContext();

  const [prevGanttId, setPrevGanttId] = useState(ganttId);

  const resetAbsencesList = () => {
    setAbsencesList([]);
    setPage(1);
  };

  const pageSize = 5;

  const { data, loading } = useQuery(GET_GANTTS_ABSENCE_BY_DATE, {
    variables: {
      payload: {
        ganttId,
        startDate,
        endDate,
        queryOptions: {
          limit: pageSize,
          sort: currentNameSort,
          skip: pageSize * currentPage - pageSize,
        },
      },
    },
    skip: !ganttId,
    fetchPolicy: 'network-only',
  });

  const { totalCount = 0, collection = [] } = data?.getGanttAbsenceRecordsByDate || {};

  useEffect(() => {
    setPage(1);
  }, []);

  useEffect(() => {
    if (ganttId !== prevGanttId) {
      resetAbsencesList();
    }
  }, [ganttId, prevGanttId]);

  useEffect(() => {
    if (totalCount > 0) {
      collection.forEach((absence: IEmployeeAbsence) => {
        setAbsencesList(prev => [...prev, transformAbsences(absence)]);
        setPrevGanttId(absence.employee.ganttId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, totalCount]);

  return {
    loading,
    absences: absencesList,
    setPage,
    paginationConfig: {
      total: totalCount,
      pageSize,
      current: currentPage,
      onChange: (page: number) => setPage(page),
    },
    onTableChange: (a: any, b: any, sorter: any) => {
      if (sorter.field) {
        setIsSorted(!isSorted);
        const sortBy = isSorted ? -1 : 1;
        setNameSort([
          { field: sorter.field === 'employee' ? 'employee.firstName' : 'date.startDate', sortBy },
        ]);
        resetAbsencesList();
      }
    },
  };
};
